// Mirrors StudentClassGroups.cs: student classes bucketed as in ln-admin's class list.
// Each bucket is ordered by StartDate, newest first.
export interface StudentClassGroups<T> {
    Waiting: T[];
    Running: T[];
    Active: T[];
    Stopped: T[];
}

export const emptyStudentClassGroups = <T,>(): StudentClassGroups<T> => ({ Waiting: [], Running: [], Active: [], Stopped: [] });

export interface StudentClassGroup<T> {
    key: keyof StudentClassGroups<T>;
    labelKey: string;
    /** Bootstrap colour, the same as ln-admin's ClassesAccordion uses for the bucket. */
    className: string;
    classes: T[];
}

const GROUPS: Omit<StudentClassGroup<unknown>, "classes">[] = [
    { key: "Waiting", labelKey: "waiting.groups", className: "light" },
    { key: "Running", labelKey: "running_courses", className: "success" },
    { key: "Active", labelKey: "active.courses", className: "warning" },
    { key: "Stopped", labelKey: "stopped_courses", className: "danger" },
];

/** The non empty groups, in ln-admin's order. The server already sorted each one. */
export function studentClassGroups<T>(classes: StudentClassGroups<T> | undefined): StudentClassGroup<T>[] {
    if (!classes) {
        return [];
    }

    return GROUPS
        .map(g => ({ ...g, classes: classes[g.key] ?? [] }))
        .filter(g => g.classes.length > 0);
}

export function countClasses<T>(classes: StudentClassGroups<T> | undefined): number {
    let count = 0;
    for (const group of studentClassGroups(classes)) {
        count += group.classes.length;
    }
    return count;
}

export function hasStudentClasses<T>(classes: StudentClassGroups<T> | undefined): boolean {
    return studentClassGroups(classes).length > 0;
}

/** The classes whose students can still reach the course, i.e. all but the stopped ones. */
export function openStudentClasses<T>(classes: StudentClassGroups<T> | undefined): T[] {
    return classes ? [...(classes.Waiting ?? []), ...(classes.Running ?? []), ...(classes.Active ?? [])] : [];
}
